import React, { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Form,
  message,
  Select,
  Spin,
  Pagination,
  Tag,
  Input,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { ManageTicket } from "../../components";
import {
  CreateTicket_api,
  FetchTicketList_api,
  UpdateTicket_api,
} from "../../Api";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

const { Option } = Select;

export const ManageTicketsPage = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTicket, setEditingTicket] = useState<any>(null);
  const [ticketForm] = Form.useForm();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword] = useDebounce(searchKeyword, 500);

  const [ticketsList, setTicketsList] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);

  // Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");

  const { refetch: FetchTickets, isFetching: isFetchingTickets } = useQuery({
    queryKey: ["tickets", page, pageSize, debouncedKeyword, statusFilter],
    queryFn: async () => {
      const response: any = await FetchTicketList_api(
        page,
        pageSize,
        debouncedKeyword,
        statusFilter,
      );
      setTicketsList(response.ticketData || []);
      setTotalTickets(response.total || 0);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    FetchTickets();
  }, [page, pageSize, debouncedKeyword, statusFilter]);

  const handleAdd = () => {
    setEditingTicket(null);
    setOpen(true);
  };

  const handleEdit = (record: any) => {
    setEditingTicket(record);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTicket(null);
    ticketForm.resetFields();
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      const values = ticketForm.getFieldsValue();

      let res;
      if (editingTicket) {
        res = await UpdateTicket_api({
          id: editingTicket.id,
          status: values.status,
          description: values.description,
          dev_comment: values.dev_comment,
        });
      } else {
        res = await CreateTicket_api(values);
      }

      if (res.success) {
        message.success(res.msg);
        handleClose();
        FetchTickets();
      } else {
        message.warning(res.msg || "Operation failed");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "red";
      case "working":
        return "blue";
      case "done":
        return "green";
      default:
        return "default";
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Ticket Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Create Ticket
        </Button>
      </div>

      <div className="flex flex-col px-6 mb-5 gap-4 md:flex-row">
        <Input
          placeholder="Search by Description or Ticket ID (TKT...)"
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-full md:w-1/3"
        />
        <Select
          defaultValue="all"
          style={{ width: 200 }}
          onChange={setStatusFilter}
        >
          <Option value="all">All Status</Option>
          <Option value="open">Open</Option>
          <Option value="working">In progress</Option>
          <Option value="done">Done</Option>
        </Select>
      </div>

      <div className="px-6 pb-6 ">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col h-[80vh]">
          <div className="flex-grow overflow-x-auto">
            {isFetchingTickets ? (
              <div className="flex items-center justify-center h-full">
                <Spin spinning={isFetchingTickets}></Spin>
              </div>
            ) : ticketsList?.length > 0 ? (
              <table className="min-w-full border-collapse text-sm overflow-x-auto">
                <thead className="bg-gray-100 text-gray-800 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="p-3 border-b text-left w-24">Ticket ID</th>
                    <th className="p-3 border-b text-left">Description</th>
                    <th className="p-3 border-b text-left">Dev Comment</th>
                    <th className="p-3 border-b text-left w-32">Status</th>
                    <th className="p-3 border-b text-left w-40">Created At</th>
                    <th className="p-3 border-b text-center w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ticketsList.map((ticket: any) => (
                    <tr key={ticket.id} className="hover:bg-gray-50 border-b">
                      <td className="p-3 font-medium">
                        {ticket.ticket_number}
                      </td>
                      <td className="p-3">
                        <div
                          className="line-clamp-2"
                          title={ticket.description}
                        >
                          {ticket.description}
                        </div>
                      </td>
                      <td className="p-3">
                        {ticket.dev_comment ? (
                          <div
                            className="line-clamp-2 text-gray-600 italic"
                            title={ticket.dev_comment}
                          >
                            {ticket.dev_comment}
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Tag color={getStatusColor(ticket.status)}>
                          {ticket.status.toUpperCase()}
                        </Tag>
                      </td>
                      <td className="p-3 text-gray-500">{ticket.created_at}</td>
                      <td className="p-3 text-center">
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(ticket)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-gray-500 h-full">
                <p>No tickets found</p>
              </div>
            )}
          </div>
          <div className="p-4 border-t bg-white flex justify-end">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={totalTickets}
              onChange={(p, ps) => {
                setPage(p);
                setPageSize(ps);
              }}
              showSizeChanger
              showTotal={(total) => `Total ${total} tickets`}
            />
          </div>
        </div>
      </div>

      <Drawer
        title={editingTicket ? "Edit Ticket" : "Create Ticket"}
        open={open}
        onClose={handleClose}
        width="40%"
        destroyOnClose
      >
        <ManageTicket
          submitting={submitting}
          form={ticketForm}
          handleFinish={handleFinish}
          initialValues={editingTicket}
        />
      </Drawer>
    </div>
  );
};